import { isAgentsEmbed } from '@/lib/agents-embed';
/**
 * Conversation state for the agent builder panel: messages, proposals and
 * their status, persisted in sessionStorage per agent ("new" for the create flow).
 * Embedded conversations stay in memory because iframe storage is optional.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  type AgentBuilderApplyResult,
  type AgentBuilderFocus,
  type AgentBuilderProposal,
} from '@/lib/api';

export type ProposalStatus = 'pending' | 'applying' | 'applied' | 'partial' | 'failed' | 'discarded';

export interface ProposalEntry {
  proposal: AgentBuilderProposal;
  status: ProposalStatus;
  result?: AgentBuilderApplyResult;
  error?: string;
}

export interface ChatEntry {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  proposals?: ProposalEntry[];
}

export interface BuilderError {
  code?: string;
  message: string;
}

const MAX_HISTORY = 40;

export const storageKey = (agentId: string | null) => `fyso.agentBuilder.${agentId ?? 'new'}`;

function load(agentId: string | null): ChatEntry[] {
  if (isAgentsEmbed()) return [];
  try {
    const raw = sessionStorage.getItem(storageKey(agentId));
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    // A reload mid-apply leaves nothing in flight.
    return parsed.map((m: ChatEntry) => ({
      ...m,
      proposals: m.proposals?.map((p) => (p.status === 'applying' ? { ...p, status: 'pending' as const } : p)),
    }));
  } catch {
    return [];
  }
}

function save(agentId: string | null, messages: ChatEntry[]) {
  if (isAgentsEmbed()) return;
  try {
    if (messages.length) sessionStorage.setItem(storageKey(agentId), JSON.stringify(messages));
    else sessionStorage.removeItem(storageKey(agentId));
  } catch {
    // storage unavailable (private mode / quota): the conversation still works in memory
  }
}

const STATUS_NOTE: Record<ProposalStatus, string> = {
  pending: 'pending review',
  applying: 'being applied',
  applied: 'applied by the user',
  partial: 'partially applied by the user',
  failed: 'apply failed',
  discarded: 'discarded by the user',
};

/** Messages as the API expects them; proposal outcomes are noted for the model. */
export function toApiMessages(messages: ChatEntry[]): Array<{ role: 'user' | 'assistant'; content: string }> {
  const out = messages.map((m) => {
    const notes = (m.proposals ?? []).map((p) => {
      const selected = p.result ? ` (${p.result.applied} of ${p.proposal.changes.length} changes)` : '';
      return `[proposal "${p.proposal.summary}": ${STATUS_NOTE[p.status]}${selected}]`;
    });
    const content = [m.content, ...notes].filter(Boolean).join('\n\n');
    return { role: m.role, content: content || '…' };
  });
  return out.slice(-MAX_HISTORY);
}

let counter = 0;
const newId = () => `m${Date.now().toString(36)}${(counter++).toString(36)}`;

export function useBuilderChat(opts: { agentId: string | null; focus: AgentBuilderFocus; locale: string }) {
  const { agentId, focus, locale } = opts;
  const [messages, setMessages] = useState<ChatEntry[]>(() => load(agentId));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<BuilderError | null>(null);
  const messagesRef = useRef(messages);
  const agentIdRef = useRef(agentId);
  const requestGeneration = useRef(0);
  agentIdRef.current = agentId;
  messagesRef.current = messages;

  useEffect(() => {
    requestGeneration.current += 1;
    agentIdRef.current = agentId;
    setMessages(load(agentId));
    setError(null);
  }, [agentId]);

  useEffect(() => {
    save(agentId, messages);
  }, [agentId, messages]);

  const request = useCallback(
    async (history: ChatEntry[]) => {
      const requestAgentId = agentId;
      const generation = requestGeneration.current;
      setLoading(true);
      setError(null);
      try {
        const res = await api.aiAgents.builder.chat({
          messages: toApiMessages(history),
          ...(agentId ? { agent_id: agentId } : {}),
          focus,
          locale,
        });
        if (agentIdRef.current !== requestAgentId || requestGeneration.current !== generation) return;
        setMessages((prev) => [
          ...prev,
          {
            id: newId(),
            role: 'assistant',
            content: res.message,
            proposals: res.proposals.map((proposal) => ({ proposal, status: 'pending' as const })),
          },
        ]);
      } catch (err) {
        const e = err as Error & { code?: string };
        if (requestGeneration.current === generation) setError({ code: e.code, message: e.message });
      } finally {
        if (requestGeneration.current === generation) setLoading(false);
      }
    },
    [agentId, focus, locale],
  );

  const send = useCallback(
    async (text: string) => {
      const content = text.trim();
      if (!content || loading) return;
      const next = [...messagesRef.current, { id: newId(), role: 'user' as const, content }];
      setMessages(next);
      await request(next);
    },
    [loading, request],
  );

  /** Re-send the conversation after an error (the last message is the user's). */
  const retry = useCallback(async () => {
    const history = messagesRef.current;
    if (!history.length || history[history.length - 1].role !== 'user' || loading) return;
    await request(history);
  }, [loading, request]);

  const updateProposal = useCallback((proposalId: string, patch: Partial<ProposalEntry>) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.proposals?.some((p) => p.proposal.id === proposalId)
          ? { ...m, proposals: m.proposals.map((p) => (p.proposal.id === proposalId ? { ...p, ...patch } : p)) }
          : m,
      ),
    );
  }, []);

  const apply = useCallback(
    async (proposal: AgentBuilderProposal, changeIds: string[]): Promise<AgentBuilderApplyResult | null> => {
      updateProposal(proposal.id, { status: 'applying', error: undefined });
      try {
        const confirmWarnings = proposal.changes.some((change) => changeIds.includes(change.id) && change.confirmation_required);
        const result = await api.aiAgents.builder.apply({
          proposal,
          change_ids: changeIds,
          locale,
          ...(confirmWarnings ? { confirm_warnings: true } : {}),
        });
        const status: ProposalStatus = result.failed === 0 ? 'applied' : result.applied > 0 ? 'partial' : 'failed';
        updateProposal(proposal.id, { status, result, ...(result.remaining_proposal ? { proposal: result.remaining_proposal } : {}) });
        return result;
      } catch (err) {
        updateProposal(proposal.id, { status: 'failed', error: (err as Error).message });
        return null;
      }
    },
    [locale, updateProposal],
  );

  const discard = useCallback((proposalId: string) => updateProposal(proposalId, { status: 'discarded' }), [updateProposal]);

  const reset = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return { messages, loading, error, send, retry, apply, discard, reset };
}
